import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Play, RefreshCw, TrendingUp, Target, Award, Info,
  Sliders, BarChart3, Zap, Save
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlgorithmPlayground = ({ token }) => {
  const [answers, setAnswers] = useState({
    q1: 'Mai-Juni',
    q2: '2',
    q4: 'mediterran & mild',
    q5: 'Strand',
    q6: 'ca_40',
    q7: '3',
    q8: '40_60k'
  });
  
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSavingWeights, setIsSavingWeights] = useState(false);
  const [algorithmSettings, setAlgorithmSettings] = useState(null);
  const [destinations, setDestinations] = useState([]);

  useEffect(() => {
    loadSettings();
    loadDestinations();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/algorithm`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlgorithmSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadDestinations = async () => {
    try {
      const response = await axios.get(`${API}/destinations`);
      setDestinations(response.data.destinations || []);
    } catch (error) {
      console.error('Error loading destinations:', error);
    }
  };

  const updateWeight = (key, value) => {
    setAlgorithmSettings(prev => ({ ...prev, [key]: parseInt(value) }));
  };

  const saveWeights = async () => {
    setIsSavingWeights(true);
    try {
      await axios.put(`${API}/admin/algorithm`, algorithmSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Gewichtungen gespeichert!');
    } catch (error) {
      console.error('Error saving weights:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSavingWeights(false);
    }
  };

  const calculateResults = async () => {
    setIsCalculating(true);
    try {
      // Convert answers to required format
      const answersArray = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const response = await axios.post(`${API}/quiz/submit`, {
        answers: answersArray,
        language: 'de'
      });
      
      setResults(response.data);
      toast.success(`${response.data.recommendations.length} Destinationen berechnet`);
    } catch (error) {
      console.error('Error calculating:', error);
      toast.error('Fehler bei der Berechnung');
    } finally {
      setIsCalculating(false);
    }
  };

  const getMatchColor = (percentage) => {
    if (percentage >= 80) return '#10B981';
    if (percentage >= 60) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <div className="algorithm-playground">
      <div className="playground-header">
        <div>
          <h2>Algorithm Playground</h2>
          <p>Simuliere Quiz-Antworten und sehe die Matching-Ergebnisse in Echtzeit</p>
        </div>
        <button 
          className="btn-primary"
          onClick={calculateResults}
          disabled={isCalculating}
        >
          {isCalculating ? (
            <>
              <RefreshCw className="spin" size={20} />
              Berechne...
            </>
          ) : (
            <>
              <Play size={20} />
              Ergebnisse berechnen
            </>
          )}
        </button>
      </div>

      <div className="playground-layout">
        {/* Left: Input Panel */}
        <div className="playground-panel">
          <h3><Sliders size={20} /> Simulierte Antworten</h3>
          
          <div className="simulated-answers">
            <div className="answer-group">
              <label>Frage 1: Saison</label>
              <select
                value={answers.q1}
                onChange={(e) => setAnswers({...answers, q1: e.target.value})}
              >
                <option value="Januar-Februar">Januar-Februar</option>
                <option value="März-April">März-April</option>
                <option value="Mai-Juni">Mai-Juni</option>
                <option value="Juli-August">Juli-August</option>
                <option value="September-Oktober">September-Oktober</option>
                <option value="November-Dezember">November-Dezember</option>
              </select>
            </div>

            <div className="answer-group">
              <label>Frage 2: Entfernung</label>
              <select
                value={answers.q2}
                onChange={(e) => setAnswers({...answers, q2: e.target.value})}
              >
                <option value="1">Nah (Europa)</option>
                <option value="2">Mittel (Mittelmeer/Nordafrika)</option>
                <option value="3">Mittel-Weit (z.B. Asien)</option>
                <option value="4">Weit (z.B. Amerika, Ozeanien)</option>
              </select>
            </div>

            <div className="answer-group">
              <label>Frage 4: Klima</label>
              <select
                value={answers.q4}
                onChange={(e) => setAnswers({...answers, q4: e.target.value})}
              >
                <option value="mediterran & mild">Mediterran & Mild</option>
                <option value="tropisch & warm">Tropisch & Warm</option>
                <option value="trocken & sonnig">Trocken & Sonnig</option>
                <option value="gemäßigt & kühl">Gemäßigt & Kühl</option>
                <option value="alpin & polar">Alpin & Polar</option>
              </select>
            </div>

            <div className="answer-group">
              <label>Frage 5: Szenario</label>
              <select
                value={answers.q5}
                onChange={(e) => setAnswers({...answers, q5: e.target.value})}
              >
                <option value="Strand">Strand</option>
                <option value="Regenwald">Regenwald</option>
                <option value="Berge">Berge</option>
                <option value="City">City</option>
                <option value="Wüste">Wüste</option>
              </select>
            </div>

            <div className="answer-group">
              <label>Frage 6: Gäste</label>
              <select
                value={answers.q6}
                onChange={(e) => setAnswers({...answers, q6: e.target.value})}
              >
                <option value="zu_zweit">Zu zweit</option>
                <option value="ca_15">Ca. 15 Gäste</option>
                <option value="ca_25">Ca. 25 Gäste</option>
                <option value="ca_40">Ca. 40 Gäste</option>
                <option value="mehr_40">Mehr als 40 Gäste</option>
              </select>
            </div>

            <div className="answer-group">
              <label>Frage 7: Dauer (Tage)</label>
              <select
                value={answers.q7}
                onChange={(e) => setAnswers({...answers, q7: e.target.value})}
              >
                <option value="1">1 Tag</option>
                <option value="2">2 Tage</option>
                <option value="3">3 Tage</option>
                <option value="4">4 Tage</option>
              </select>
            </div>

            <div className="answer-group">
              <label>Frage 8: Budget</label>
              <select
                value={answers.q8}
                onChange={(e) => setAnswers({...answers, q8: e.target.value})}
              >
                <option value="bis_25k">Bis 25.000€</option>
                <option value="20_40k">20.000€ - 40.000€</option>
                <option value="25_40k">25.001€ - 40.000€</option>
                <option value="40_60k">40.001€ - 60.000€</option>
                <option value="60_90k">60.001€ - 90.000€</option>
              </select>
            </div>
          </div>

          {algorithmSettings && (
            <div className="algorithm-info" data-testid="weights-editor">
              <div className="weights-header">
                <h4><Sliders size={16} /> Gewichtungen anpassen</h4>
                <button
                  className="btn-save-weights"
                  onClick={saveWeights}
                  disabled={isSavingWeights}
                  data-testid="save-weights-btn"
                >
                  {isSavingWeights ? <RefreshCw className="spin" size={14} /> : <Save size={14} />}
                  Speichern
                </button>
              </div>
              <div className="weights-sliders">
                {[
                  { key: 'season_weight', label: 'Saison' },
                  { key: 'distance_weight', label: 'Entfernung' },
                  { key: 'budget_weight', label: 'Budget' },
                  { key: 'climate_weight', label: 'Klima' },
                  { key: 'scenario_weight', label: 'Szenario' },
                  { key: 'cultural_weight', label: 'Kultur' },
                  { key: 'guest_capacity_weight', label: 'Gästekapazität' },
                ].map(({ key, label }) => (
                  <div key={key} className="weight-slider-item" data-testid={`weight-${key}`}>
                    <div className="weight-slider-label">
                      <span>{label}</span>
                      <strong>{algorithmSettings[key]}</strong>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={algorithmSettings[key] || 0}
                      onChange={(e) => updateWeight(key, e.target.value)}
                      className="weight-range-input"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Results Panel */}
        <div className="playground-panel">
          <h3><BarChart3 size={20} /> Berechnete Ergebnisse</h3>
          
          {!results ? (
            <div className="no-results">
              <Target size={64} color="#9CA3AF" />
              <p>Klicke auf "Ergebnisse berechnen" um die Top-Matches zu sehen</p>
            </div>
          ) : (
            <div className="results-list">
              {results.recommendations.slice(0, 10).map((rec, index) => (
                <div key={index} className="result-item">
                  <div className="result-rank">#{index + 1}</div>
                  <div className="result-info">
                    <h4>{rec.destination}</h4>
                    <div className="result-meta">
                      <span>{destinations.find(d => d.name === rec.destination)?.continent || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="result-score">
                    <div 
                      className="score-circle"
                      style={{
                        background: `conic-gradient(${getMatchColor(rec.match_percentage)} ${rec.match_percentage * 3.6}deg, #f3f4f6 0deg)`
                      }}
                    >
                      <div className="score-inner">
                        {rec.match_percentage.toFixed(0)}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {results && (
            <div className="results-summary">
              <h4><Award size={16} /> Zusammenfassung</h4>
              <div className="summary-stats">
                <div className="summary-stat">
                  <span>Höchster Match:</span>
                  <strong>{results.recommendations[0]?.match_percentage.toFixed(1)}%</strong>
                </div>
                <div className="summary-stat">
                  <span>Niedrigster Match (Top 5):</span>
                  <strong>
                    {results.recommendations[4]?.match_percentage.toFixed(1) || 'N/A'}%
                  </strong>
                </div>
                <div className="summary-stat">
                  <span>Durchschnitt (Top 5):</span>
                  <strong>
                    {(results.recommendations.slice(0, 5)
                      .reduce((sum, r) => sum + r.match_percentage, 0) / 
                      Math.min(5, results.recommendations.length)).toFixed(1)}%
                  </strong>
                </div>
                <div className="summary-stat">
                  <span>Gesamt Destinationen:</span>
                  <strong>{results.recommendations.length}</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="playground-tips">
        <h4><Zap size={16} /> Tipps</h4>
        <ul>
          <li>Ändere die Antworten und berechne neu, um verschiedene Szenarien zu testen</li>
          <li>Die Gewichtungen können im "Algorithmus"-Tab angepasst werden</li>
          <li>Match-Percentage unter 70% deutet auf Optimierungsbedarf hin</li>
          <li>Teste kritische Kombinationen wie "Januar-Februar + Nah"</li>
        </ul>
      </div>
    </div>
  );
};

export default AlgorithmPlayground;
